import { mdiAlertCircle, mdiCheckCircle, mdiChevronRight, mdiCog, mdiEye, mdiEyeOff, mdiRefresh, } from "@mdi/js";
import React, { useCallback, useMemo, useState, } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { setOpenMainPage, setSettingsPage } from "../../../actions/session";
import { Button } from "../../../ui/components/button/Button";
import { Icon } from "../../../ui/components/icon/Icon";
import { NoResults } from "../../../ui/components/no_results/NoResults";
import { Pictogram } from "../../../ui/components/pictogram/Pictogram";
import { TabButton } from "../../../ui/components/tabs/Tab";
import { TabBar } from "../../../ui/components/tabs/TabBar";
import { TabPanel } from "../../../ui/components/tabs/TabPanel";
import { TabProvider } from "../../../ui/components/tabs/tabs.context";
import { Typography } from "../../../ui/components/typography/Typography";
import { batchDispatch } from "../../../util/util";
import MainPage from "../../../views/MainPage";
import { setRequirementHidden, clearAllHiddenRequirements, } from "../actions/persistent";
import { hiddenRequirements, allModRequirements } from "../selectors";
import HealthCheckDetailPage from "./HealthCheckDetailPage";
const Mod = ({ isHidden, onClick, onToggleHide, requirementInfo, }) => {
    const { t } = useTranslation("health_check");
    return (React.createElement("div", { className: "hover-overlay-weak flex w-full cursor-pointer items-center gap-x-4 rounded-sm bg-surface-mid px-4 py-3 shadow-xs focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-info-subdued", role: "button", tabIndex: 0, onClick: onClick, onKeyDown: (e) => {
            if (["Enter", " "].includes(e.key)) {
                e.preventDefault();
                onClick();
            }
        } },
        React.createElement(Icon, { className: "shrink-0 text-info-strong", path: mdiAlertCircle }),
        React.createElement("div", { className: "grow space-y-0.5 text-left" },
            React.createElement(Typography, null, t("listing::item::title", {
                modName: requirementInfo.requiredBy.modName,
            })),
            React.createElement(Typography, { appearance: "subdued", typographyType: "body-sm" }, t("listing::item::description", {
                dependencyModName: requirementInfo.modName ||
                    requirementInfo.modUrl ||
                    requirementInfo.notes,
            }))),
        React.createElement(Button, { buttonType: "tertiary", filled: "weak", leftIconPath: isHidden ? mdiEye : mdiEyeOff, size: "sm", title: isHidden ? t("common:::unhide") : t("common:::hide"), onClick: (e) => {
                e.stopPropagation();
                onToggleHide?.(e);
            } }),
        React.createElement(Icon, { className: "shrink-0 text-translucent-moderate", path: mdiChevronRight, size: "lg" })));
};
function HealthCheckPage({ api, onRefresh, onDownloadRequirement, }) {
    const { t } = useTranslation(["health_check", "common"]);
    const dispatch = useDispatch();
    const [showDetail, setShowDetail] = useState(false);
    const [selectedTab, setSelectedTab] = useState("active");
    const [selectedRequirement, setSelectedRequirement] = useState(null);
    const modRequirements = useSelector(allModRequirements);
    const hiddenReqsMap = useSelector(hiddenRequirements);
    const isModRequirementHidden = useCallback((mod) => {
        const hiddenReqs = hiddenReqsMap[mod.requiredBy.modId] || [];
        return hiddenReqs.includes(mod.id);
    }, [hiddenReqsMap]);
    // Filter active and hidden mods
    const activeMods = useMemo(() => modRequirements.filter((mod) => !isModRequirementHidden(mod)), [modRequirements, isModRequirementHidden]);
    const hiddenMods = useMemo(() => modRequirements.filter((mod) => isModRequirementHidden(mod)), [modRequirements, isModRequirementHidden]);
    const toggleHideMod = (mod) => {
        const isHidden = isModRequirementHidden(mod);
        if (isHidden) {
            // Unhide: clear all hidden dependencies for this mod
            dispatch(setRequirementHidden(mod.requiredBy.modId, mod.id, false));
        }
        else {
            dispatch(setRequirementHidden(mod.requiredBy.modId, mod.id, true));
        }
    };
    const hideAllActive = () => {
        const batched = [];
        activeMods.forEach((mod) => {
            batched.push(setRequirementHidden(mod.requiredBy.modId, mod.id, true));
        });
        batchDispatch(dispatch, batched);
    };
    const unhideAll = () => {
        dispatch(clearAllHiddenRequirements(undefined));
    };
    if (showDetail && selectedRequirement) {
        return (React.createElement(HealthCheckDetailPage, { api: api, mod: selectedRequirement, onBack: () => {
                setShowDetail(false);
                setSelectedRequirement(null);
            }, onDownloadMod: (mod, file) => onDownloadRequirement?.(mod, file) }));
    }
    const activeCount = activeMods.length;
    const hiddenCount = hiddenMods.length;
    return (React.createElement(MainPage, { id: "health-check-page" },
        React.createElement(MainPage.Body, null,
            React.createElement("div", { className: "h-full space-y-4 overflow-y-auto p-6" },
                React.createElement("div", { className: "flex items-center gap-x-6" },
                    React.createElement("div", { className: "flex grow items-center gap-x-2" },
                        React.createElement(Pictogram, { name: "health-check", size: "sm" }),
                        React.createElement("div", { className: "grow" },
                            React.createElement("div", { className: "flex items-center gap-x-1.5" },
                                React.createElement(Typography, { as: "h2", className: "m-0", typographyType: "heading-xs" }, t("listing::title")),
                                React.createElement(Typography, { as: "div", className: "justity-center flex min-h-4 items-center rounded-sm border border-neutral-strong px-1 leading-4", typographyType: "title-xs" }, t("common:::beta"))),
                            React.createElement(Typography, { appearance: "moderate" }, t("listing::subtitle")))),
                    React.createElement("div", { className: "flex shrink-0 gap-x-2" },
                        React.createElement(Button, { buttonType: "tertiary", filled: "weak", leftIconPath: mdiRefresh, size: "sm", title: t("common:::refresh"), onClick: () => onRefresh?.() }),
                        React.createElement(Button, { buttonType: "tertiary", filled: "weak", leftIconPath: mdiCog, size: "sm", title: t("common:::settings"), onClick: () => {
                                dispatch(setOpenMainPage("application_settings", false));
                                dispatch(setSettingsPage("Vortex"));
                            } }))),
                React.createElement(TabProvider, { tab: selectedTab, tabListId: "health-check-mods", tabType: "secondary", onSetSelectedTab: setSelectedTab },
                    React.createElement("div", { className: "flex items-center justify-between" },
                        React.createElement(TabBar, null,
                            React.createElement(TabButton, { count: activeMods.length, name: t("common:::active") }),
                            React.createElement(TabButton, { count: hiddenMods.length, name: t("common:::hidden") })),
                        React.createElement(Button, { buttonType: "tertiary", disabled: (selectedTab === "active" && !activeCount) ||
                                (selectedTab === "hidden" && !hiddenCount), filled: "weak", leftIconPath: selectedTab === "active" ? mdiEyeOff : mdiEye, size: "sm", onClick: selectedTab === "active" ? hideAllActive : unhideAll }, selectedTab === "active"
                            ? `${t("common:::hide_all")}${activeCount ? ` (${activeCount})` : ""}`
                            : `${t("common:::unhide_all")}${hiddenCount ? ` (${hiddenCount})` : ""}`)),
                    React.createElement(TabPanel, { name: "active" }, !activeCount ? (React.createElement(NoResults, { appearance: "success", className: "py-24", iconPath: mdiCheckCircle, message: t("listing::no_results_active::message"), title: t("listing::no_results_active::title") })) : (React.createElement("div", { className: "space-y-2" }, activeMods.map((mod) => (React.createElement(Mod, { key: `${mod.requiredBy.modId}-${mod.uid || `${mod.gameId}-${mod.modId || mod.modName}`}`, requirementInfo: mod, onClick: () => {
                            setSelectedRequirement(mod);
                            setShowDetail(true);
                        }, onToggleHide: () => toggleHideMod(mod) })))))),
                    React.createElement(TabPanel, { name: "hidden" }, !hiddenCount ? (React.createElement(NoResults, { className: "py-24", iconPath: mdiEyeOff, title: t("listing::no_results_hidden::title") })) : (React.createElement("div", { className: "space-y-2" }, hiddenMods.map((mod) => (React.createElement(Mod, { isHidden: true, key: `${mod.requiredBy.modId}-${mod.uid || `${mod.gameId}-${mod.modId || mod.modName}`}`, requirementInfo: mod, onClick: () => {
                            setSelectedRequirement(mod);
                            setShowDetail(true);
                        }, onToggleHide: () => toggleHideMod(mod) })))))))))));
}
export default HealthCheckPage;
